/**
 * De spelregels van de Krabbelgotchi, los van het scherm.
 *
 * Alles hier is een pure functie op een gewoon object, zodat de regels in
 * tests/tamagotchi.test.mjs zonder browser nagelopen kunnen worden. Het bewaren,
 * de klok en het tekenen staan in logic/tamagotchi.js.
 *
 * ---- de tijd doet het werk ----------------------------------------------
 *
 * Een beestje onthoudt geen balkjes maar tijdstippen: wanneer het at, wanneer je
 * ermee speelde en wanneer je de hokjes schoonmaakte. Hoe het ervoor staat wordt
 * daaruit berekend op het moment dat je kijkt. Dat is de enige manier waarop het
 * ook doorloopt terwijl de app dicht is, en het scheelt een klok die de hele dag
 * balkjes moet aftellen.
 *
 * ---- het gaat niet dood -------------------------------------------------
 *
 * Een echte tamagotchi gaat dood als je een weekend wegblijft. Op een kantoor
 * met vierentwintig mensen is dat geen leuke grap maar een vervelend bericht op
 * maandag. Dit beestje wordt hongerig, verdrietig en uiteindelijk ziek, en het
 * knapt weer op zodra je iets doet.
 *
 * ---- het plaatje is uitgerekend, niet getekend --------------------------
 *
 * De pixels komen uit pixels(): een ovaal lijf, twee oortjes, ogen en een mond.
 * Uitgerekend en niet met de hand ingetikt, want zestien regels tekst per stand
 * per humeur zijn nooit symmetrisch en nooit te veranderen.
 */

/**
 * Het veld: 16 bij 16, met de acht kleuren van het tekendoek.
 *
 * Zestien en niet vierentwintig. Op vierentwintig kon het beestje netjes rond
 * zijn, en juist daardoor leek het op niets: een tamagotchi is grof. Op zestien
 * is elke pixel een keuze en krijg je die hoekige beestjes van 1997 terug.
 */
/**
 * Het schermpje: 24 bij 24 vakjes, net als Krabbelworm.
 *
 * Het was 16 bij 16. Er kwamen vier rijen bij voor de twee meters, en daarna nog
 * vier zodat dit veld even groot is als dat van de worm. Dat is geen sier: staan
 * twee spelletjes in dezelfde hal met hetzelfde soort behuizing, dan hoort het
 * schermpje er even groot in te zitten, en met hetzelfde aantal vakjes rekent de
 * maat in logic/tamagotchi.js precies hetzelfde uit als die in logic/snake.js.
 *
 * Het beestje houdt onderaan zijn veertien rijen; wat erbij kwam is lucht boven
 * zijn kruin en de kop met de meters.
 */
export const VELD = 24;

/**
 * De rijen die de meters innemen, plus lucht.
 *
 * Rij 0 is lucht, rij 1 tot en met 3 zijn de twee meters naast elkaar met de balk
 * op rij 2. Ze lagen tegen de bovenrand aan en dat plakte; die ene rij lucht
 * erboven kost het beestje niets, want de vloer schuift mee naar beneden. Alles wat het beestje omhoog stuurt (een Z'je, een wolkje, een hartje)
 * mag hier niet in komen, dus dat klemt op KOP.
 */
export const KOP = 4;

/**
 * De twee meters staan naast elkaar, niet onder elkaar.
 *
 * Onder elkaar kostten ze acht rijen, en dan bleef er voor het beestje zo weinig
 * over dat hij verdronk in zijn eigen scherm. Naast elkaar kost het er vier, en
 * dat is precies genoeg voor een tekentje van drie bij drie met een balkje
 * ernaast. Elke meter is dus een blok van tien breed: drie tekentje, een gat, zes
 * balk.
 *
 * Als constanten en niet als losse getallen in de tekenfunctie, want de tests
 * meten deze plekken na.
 */
export const METER_RIJ = 2;
export const METERS = [{ tekenX: 1, balkX: 5 }, { tekenX: 13, balkX: 17 }];
export const METER_BREED = 6;

/** Kleuren als plek in het palet; zie lib/palette.js. */
export const K = {
  zwart: 0, grijs: 1, blauw: 2, wijn: 3, groen: 4, rood: 5, oranje: 6, lucht: 7
};

/** Van vol naar leeg: honger in acht uur, humeur in twaalf. */
export const HONGER_UREN = 8;
export const BLIJ_UREN = 12;
/**
 * Elke vier uur een hoopje, en er passen er twee.
 *
 * Op een vloertje van zestien pixels staat het beestje in het midden en is er
 * links en rechts van hem plek voor precies één. Een derde zou achter hem
 * verdwijnen, en een hoopje dat je niet ziet is geen hoopje.
 */
export const HOOP_UREN = 4;
export const HOOP_MAX = 2;

/** Het ei komt na twee minuten uit; daarna groeit het per dag. */
export const EI_MIN = 2;
export const KIND_DAGEN = 1;
export const GROOT_DAGEN = 3;

const UUR = 3600e3;
const klem = (n, laag, hoog) => Math.max(laag, Math.min(hoog, n));

/** Een nieuw beestje: alles net gedaan, dus het begint tevreden. */
/**
 * Een nieuw beestje.
 *
 * `soort` is welk van de zes je krijgt. Die komt van buiten en wordt hier niet
 * geloot: deze functie moet elke keer hetzelfde teruggeven, anders is er geen test
 * op te schrijven. De loting staat in openTamagotchi.
 */
export function nieuwBeest(nu, soort) {
  return {
    geboren: nu, gevoed: nu, gespeeld: nu, geschept: nu, licht: true, naam: '',
    soort: soortVanBeest({ soort })
  };
}

/**
 * Een naam is precies drie letters, zoals op een arcadekast.
 *
 * Niet omdat er geen plek is voor meer, maar omdat drie letters kiezen iets anders
 * is dan een naam typen: je draait aan drie wieltjes tot er iets staat wat je leuk
 * vindt. Dat hoort bij een apparaatje met knopjes, en het levert altijd een naam op
 * die onder het schermpje past.
 */
export const NAAM_MAX = 3;
export const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * Een naam opschonen: alleen letters, precies drie, en als woord geschreven.
 *
 * In beeld staat hij in hoofdletters op de wieltjes, maar in een zin staat hij als
 * naam: "Pip heeft honger" en niet "PIP heeft honger". Dat laatste leest als
 * schreeuwen.
 */
export function naamOk(tekst) {
  const kaal = String(tekst || '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, NAAM_MAX);
  if (!kaal) return '';
  return kaal[0] + kaal.slice(1).toLowerCase();
}

/** Van drie wieltjes naar een naam. */
export function naamVanLetters(letters) {
  return naamOk((letters || []).join(''));
}

/** En terug: van een naam naar de stand van de drie wieltjes. */
export function lettersVanNaam(naam) {
  const kaal = String(naam || '').toUpperCase().replace(/[^A-Z]/g, '');
  return [0, 1, 2].map((i) => kaal[i] || 'A');
}

/**
 * Een lettertype van drie bij vijf, alleen hoofdletters.
 *
 * Drie pixels breed is het smalste waarop een A nog van een H te onderscheiden is,
 * en met drie letters van drie plus twee kieren past een naam precies in het midden
 * van een scherm van vierentwintig.
 */
export const FONT = {
  A: ['.#.', '#.#', '###', '#.#', '#.#'],
  B: ['##.', '#.#', '##.', '#.#', '##.'],
  C: ['.##', '#..', '#..', '#..', '.##'],
  D: ['##.', '#.#', '#.#', '#.#', '##.'],
  E: ['###', '#..', '##.', '#..', '###'],
  F: ['###', '#..', '##.', '#..', '#..'],
  G: ['.##', '#..', '#.#', '#.#', '.##'],
  H: ['#.#', '#.#', '###', '#.#', '#.#'],
  I: ['###', '.#.', '.#.', '.#.', '###'],
  J: ['..#', '..#', '..#', '#.#', '.#.'],
  K: ['#.#', '#.#', '##.', '#.#', '#.#'],
  L: ['#..', '#..', '#..', '#..', '###'],
  M: ['#.#', '###', '###', '#.#', '#.#'],
  N: ['#.#', '##.', '#.#', '#.#', '#.#'],
  O: ['.#.', '#.#', '#.#', '#.#', '.#.'],
  P: ['##.', '#.#', '##.', '#..', '#..'],
  Q: ['.#.', '#.#', '#.#', '.#.', '..#'],
  R: ['##.', '#.#', '##.', '#.#', '#.#'],
  S: ['.##', '#..', '.#.', '..#', '##.'],
  T: ['###', '.#.', '.#.', '.#.', '.#.'],
  U: ['#.#', '#.#', '#.#', '#.#', '###'],
  V: ['#.#', '#.#', '#.#', '#.#', '.#.'],
  W: ['#.#', '#.#', '###', '###', '#.#'],
  X: ['#.#', '#.#', '.#.', '#.#', '#.#'],
  Y: ['#.#', '#.#', '.#.', '.#.', '.#.'],
  Z: ['###', '..#', '.#.', '#..', '###']
};

/** Eén wieltje een slag verder of terug, en het loopt rond. */
export function draaiLetter(letter, richting) {
  const i = LETTERS.indexOf(String(letter || 'A').toUpperCase());
  const n = (i < 0 ? 0 : i) + (richting < 0 ? -1 : 1);
  return LETTERS[(n + LETTERS.length) % LETTERS.length];
}

/** Hoe je over hem praat: bij naam, of anders gewoon "hij". */
export function noem(beest) {
  return (beest && naamOk(beest.naam)) || 'Hij';
}

/**
 * De vier fases op volgorde, zodat je kunt zien of hij vooruit is gegaan.
 *
 * `groeide(oud, nieuw)` is waar als hij een stap verder is. Niet of hij anders is:
 * terug kan niet, maar een lege of onbekende fase moet geen feest opleveren.
 */
export const FASES = ['ei', 'baby', 'kind', 'groot'];
export function groeide(vorige, nu) {
  const a = FASES.indexOf(vorige);
  const b = FASES.indexOf(nu);
  return a >= 0 && b > a;
}

/** De vier tijdstippen die een beestje maken. `licht` hoort er niet bij: dat is geen moment. */
export const MOMENTEN = ['geboren', 'gevoed', 'gespeeld', 'geschept'];

/** Is dit een bruikbaar beestje? Alle vier de tijdstippen moeten er zijn. */
export function beestOk(b) {
  return !!b && MOMENTEN.every((k) => typeof b[k] === 'number' && isFinite(b[k]));
}

/** Wanneer er voor het laatst iets met dit beestje gebeurde. */
export function laatsteRoer(b) {
  return beestOk(b) ? Math.max(b.geboren, b.gevoed, b.gespeeld, b.geschept) : 0;
}

/**
 * Van twee versies van hetzelfde beestje: welke is de verste?
 *
 * Nodig zodra het beestje ook op de server staat. Speel je op je telefoon en open
 * je daarna je computer, dan bestaat hij twee keer, en dan moet er iets kiezen. De
 * verste wint: die waar het laatst iets is gebeurd. Zou de server altijd winnen,
 * dan draait het beestje terug zodra je hem op een tweede apparaat opent voordat
 * je vorige beurt was doorgegeven.
 *
 * Gelijkspel gaat naar de eerste, want dan maakt het niet uit en is niets doen het
 * rustigste.
 */
export function verste(a, b) {
  if (!beestOk(a)) return beestOk(b) ? b : null;
  if (!beestOk(b)) return a;
  return laatsteRoer(b) > laatsteRoer(a) ? b : a;
}

/**
 * Hoe het ervoor staat op tijdstip `nu`.
 *
 * `uur` is het uur van de dag, 0 tot 23, en dat komt van buiten: een pure
 * functie mag niet zelf op de klok kijken. Tussen elf 's avonds en zeven 's
 * ochtends slaapt het beestje, en dan verandert er niets aan zijn humeur.
 */
export function standVan(beest, nu, uur = 12) {
  const honger = klem(100 - ((nu - beest.gevoed) / (HONGER_UREN * UUR)) * 100, 0, 100);
  const blij = klem(100 - ((nu - beest.gespeeld) / (BLIJ_UREN * UUR)) * 100, 0, 100);
  const hoopjes = klem(Math.floor((nu - beest.geschept) / (HOOP_UREN * UUR)), 0, HOOP_MAX);
  const minuten = (nu - beest.geboren) / 60e3;
  const dagen = minuten / (60 * 24);
  const fase = minuten < EI_MIN ? 'ei'
    : dagen < KIND_DAGEN ? 'baby'
      : dagen < GROOT_DAGEN ? 'kind'
        : 'groot';
  // Het slaapt 's nachts, en ook als jij het licht uitdoet. Dat tweede is de
  // lampknop van een echte tamagotchi: een middagslaapje op commando.
  const slaapt = fase !== 'ei' && (uur >= 23 || uur < 7 || beest.licht === false);
  const humeur = fase === 'ei' ? 'ei'
    : slaapt ? 'slaapt'
      : (honger < 15 && hoopjes >= HOOP_MAX) ? 'ziek'
        : honger < 30 ? 'hongerig'
          : blij < 30 ? 'verdrietig'
            : 'blij';
  return { honger, blij, hoopjes, minuten, dagen, fase, humeur, slaapt };
}

export function voeren(beest, nu) { return { ...beest, gevoed: nu }; }

/**
 * Hoeveel de hartmeter bijkomt van aaien en van spelen.
 *
 * Beide vullen bij en zetten hem niet vol. Spelen zette de klok op nu, en dan
 * sprong de meter in één klik van leeg naar vol: één keer klikken en je was
 * klaar. Nu is het spelletje de helft van de meter en een aai een kwart, dus twee
 * spelletjes vullen hem en aaien blijft het kleine gebaar ertussen.
 *
 * De meter is zes pixels breed (`METER_BREED`), dus een kwart is anderhalve pixel
 * en dat is het minste dat je nog ziet bewegen. Kleiner maken heeft geen zin: dan
 * klik je en verandert er niets in beeld.
 */
export const AAI_UREN = BLIJ_UREN / 4;
export const SPEEL_UREN = BLIJ_UREN / 2;

/**
 * Zoveel uur bij de hartmeter, gerekend vanaf de stand die je nu ziet.
 *
 * Dat laatste is het punt. De meter is een klok: `gespeeld` is het moment van de
 * laatste speelbeurt en de meter loopt daarvandaan in twaalf uur leeg. Uren bij die
 * klok optellen werkt alleen zolang hij nog niet leeg is; van een beestje dat een
 * maand niet gezien is ligt die klok een maand terug, en dan schuift "zes uur erbij"
 * de meter geen pixel. Twee spelletjes zouden er dan honderdtwintig worden.
 *
 * Dus eerst kijken waar de meter staat, daar het stukje bij optellen, en de klok
 * daarna terugrekenen uit de nieuwe stand. Boven de honderd kan hij niet komen: dan
 * zou de meter daarna een tijd blijven staan in plaats van te zakken.
 */
function blijerVan(beest, nu, uren) {
  const nivo = klem(100 - ((nu - beest.gespeeld) / (BLIJ_UREN * UUR)) * 100, 0, 100);
  const na = klem(nivo + (uren / BLIJ_UREN) * 100, 0, 100);
  return { ...beest, gespeeld: nu - (1 - na / 100) * BLIJ_UREN * UUR };
}

/**
 * Aaien: een kleine oppepper, geen volle speelbeurt.
 *
 * Een kwart meter erbij. Zo helpt aaien wel, maar blijft spelen het echte werk en
 * heeft het zin om beide te doen. Staat de meter al vol, dan doet aaien niets, en
 * dat hoort.
 */
export function aaien(beest, nu) { return blijerVan(beest, nu, AAI_UREN); }

/**
 * Twee versies van hetzelfde beestje samenvoegen: per klok de nieuwste.
 *
 * `verste` koos één hele kopie, die waar het laatst iets gebeurde. Dat gaat mis zodra
 * twee mensen tegelijk iets doen: voert een collega jouw beestje terwijl jij hem aait,
 * dan wint jouw kopie (jouw aai is later) en gaat zijn maaltijd verloren, want jouw
 * kopie kent die niet. De drie klokken lopen alleen vooruit, dus van elk de grootste
 * nemen verliest niets.
 *
 * `gevoerdDoor` hoort bij `gevoed`: wie de laatste hap gaf staat bij die hap. Naam,
 * soort, geboorte en de lamp komen van de server. De lamp ging eerst mee met de kopie
 * die het laatst iets deed, maar de lamp zelf verzet geen enkele klok: deed je hem op
 * je telefoon uit en opende je daarna je computer, dan maakte het openingsdeuntje de
 * kopie op je computer "de nieuwste" en ging het licht weer aan. Een lampklik die hier
 * nog onderweg is houdt tamHaal zelf vast.
 */
export function samenvoegen(hier, server) {
  if (!beestOk(hier)) return beestOk(server) ? server : null;
  if (!beestOk(server)) return hier;
  const winnaar = verste(hier, server);
  const hapServer = server.gevoed >= hier.gevoed;
  return {
    ...winnaar,
    geboren: server.geboren,
    gevoed: Math.max(hier.gevoed, server.gevoed),
    gevoerdDoor: (hapServer ? server.gevoerdDoor : hier.gevoerdDoor) || '',
    gespeeld: Math.max(hier.gespeeld, server.gespeeld),
    geschept: Math.max(hier.geschept, server.geschept),
    // Gelijkgetrokken naar waar of onwaar. Een kopie uit een oude browser heeft geen
    // `licht`, en `undefined` tegen `true` van de server telde als een verschil: dan
    // werd `!!undefined` teruggestuurd en ging bij iemand het licht uit. Ontbreken
    // betekent aan, net als in standVan.
    licht: server.licht !== false,
    naam: server.naam, soort: server.soort
  };
}

/** De velden die op de server staan, voor een PATCH met alleen wat veranderde. */
export const BEEST_VELDEN = ['geboren', 'gevoed', 'gespeeld', 'geschept', 'licht', 'naam', 'soort', 'gevoerdDoor'];

/**
 * Wat er naar de server gaat.
 *
 * Alles bij het aanmaken, want dan is er nog niets. Bij bijwerken alleen de velden
 * die de handeling raakte, zodat een aai nooit de hap van een collega overschrijft;
 * zie tamStuur in logic/tamagotchi.js. `authorId` gaat altijd mee.
 */
export function naarServer(b, velden, authorId, bijwerken) {
  const alles = {
    authorId,
    geboren: b.geboren, gevoed: b.gevoed, gespeeld: b.gespeeld, geschept: b.geschept,
    licht: b.licht !== false, naam: naamOk(b.naam), soort: soortVanBeest(b),
    gevoerdDoor: b.gevoerdDoor || ''
  };
  if (!bijwerken || !velden || !velden.length) return alles;
  const uit = { authorId };
  velden.forEach((k) => { if (k in alles) uit[k] = alles[k]; });
  return uit;
}

/** Welke van die velden in `a` anders zijn dan in `b`. */
export function verschil(a, b) {
  return BEEST_VELDEN.filter((k) => (a && a[k]) !== (b && b[k]));
}

/** De lamp aan of uit. Uit betekent slapen, ook midden op de dag. */
export function lamp(beest) {
  return { ...beest, licht: beest.licht === false };
}

/**
 * Wat het weegt, in gram.
 *
 * Verzonnen maar niet willekeurig: het groeit met de dagen en zakt als de buik
 * leeg is. Op een echte tamagotchi is de weegschaal ook vooral een grapje.
 */
export function gewicht(beest, nu) {
  const stand = standVan(beest, nu, 12);
  return Math.max(5, 8 + Math.floor(stand.dagen * 4) + Math.round(stand.honger / 12));
}
/** Spelen: de halve meter erbij, dus twee aaien waard. Zie AAI_UREN hierboven. */
export function spelen(beest, nu) { return blijerVan(beest, nu, SPEEL_UREN); }

export function scheppen(beest, nu) { return { ...beest, geschept: nu }; }

/** Hoe breed het lijf is per stand, gerekend vanaf het midden. */
/**
 * Hoe groot het beestje is per fase, als straal.
 *
 * Deze getallen hoorden bij een veld van zestien. Op vierentwintig bleef het
 * beestje even groot en werd hij dus klein in zijn eigen scherm: tien vakjes
 * breed op zestien is ruim de helft, op vierentwintig nog geen kwart. Anderhalf
 * keer zo groot, dus, en afgerond op hele vakjes.
 *
 * Zeven is de bovengrens, en die is uitgerekend en niet gegokt: onder de kop en
 * boven de vloer passen negentien rijen, en het beestje is er 2r + 4 hoog: het
 * lijf, twee oortjes erboven en een rijtje pootjes eronder. Bij acht wordt dat
 * twintig en stoot hij bij het opwippen tegen zijn eigen meters.
 */
const MAAT = { ei: 4, baby: 5, kind: 6, groot: 7 };

/**
 * De pixels van het beestje en zijn hokje.
 *
 * Levert een lijst van { x, y, k }: plek op het veld en kleur als plek in het
 * palet. `tik` laat het beestje op en neer wippen, één pixel per halve seconde;
 * slapend wipt het niet maar ademt het. `actie` is wat je net indrukte, en dat
 * legt er een appeltje of een hartje bij.
 *
 * ---- licht lijf, donkere omlijning --------------------------------------
 *
 * Eerst was het lijf donker en dat werd een blok. De poppetjes op een echte
 * tamagotchi zijn juist licht met een donker randje eromheen, en dat randje maakt
 * er een tekeningetje van in plaats van een silhouet. Het randje is hier
 * uitgerekend: een vakje hoort bij de rand zodra er naast, boven of onder een
 * vakje ligt dat niet meer bij het lijf hoort.
 *
 * ---- waarom het lijf een blokje met ronde hoeken is ---------------------
 *
 * Een ovaal ziet er op zestien pixels uit als een vlek. Dit is een superellips
 * met een hoge macht, en die geeft een blok met de hoeken eraf: hoekig, zoals het
 * hoort.
 */
/**
 * @param deel  'alles' tekent het hele beeld, 'lijf' laat het gezicht weg en
 *              'gezicht' tekent alleen de ogen en de mond. Dat laatste is er voor
 *              de tekenaar: die knipt per stukje uit wat de code nu tekent, en dan
 *              moet een lijf zonder gezicht komen en een gezicht zonder lijf.
 *              Anders krijg je een lijf met een gezicht ingebakken en tekent de app
 *              er nog een tweede gezicht overheen.
 */
export function pixels(stand, tik = 0, actie = null, naamKiezer = null, deel = 'alles') {
  const uit = [];
  const bezet = new Set();
  const zet = (x, y, k) => {
    if (x < 0 || y < 0 || x >= VELD || y >= VELD) return;
    bezet.add(x + ',' + y);
    uit.push({ x, y, k });
  };
  /**
   * Hetzelfde, maar alleen op een leeg vakje. Op zestien pixels is er naast het
   * beestje amper ruimte voor een appel, dus liever een appel die deels achter
   * hem verdwijnt dan een appel die een hap uit zijn zij neemt.
   */
  const zetAchter = (x, y, k) => {
    if (bezet.has(x + ',' + y)) return;
    zet(x, y, k);
  };

  /**
   * ---- de twee meters ----------------------------------------------------
   *
   * Buik en humeur stonden als html-balkjes onder het schermpje, met een kopje
   * ernaast van tien pixels. Nu staan ze erin, in pixels, zoals op een echte
   * tamagotchi: een rij die volloopt.
   *
   * Onder een kwart worden ze rood. Dat is het verschil tussen "het loopt leeg"
   * en "doe er iets aan", en op deze maat zie je een korte balk sneller aan zijn
   * kleur dan aan zijn lengte.
   */
  const meter = (n, teken, pct, kleur) => {
    const plek = METERS[n];
    // Het tekentje, drie bij drie. Welk balkje welk is, las je eerst aan niets:
    // twee gekleurde streepjes zonder kopje. Nu staat er het tekentje van de knop
    // waarmee je hem vult, dus het appeltje hoort bij voeren en het hartje bij
    // aaien.
    for (const [dx, dy, k] of teken) zet(plek.tekenX + dx, METER_RIJ - 1 + dy, k);
    // De balk ernaast, op de middelste van de drie rijen.
    const vol = Math.round(klem(pct, 0, 100) / 100 * METER_BREED);
    const laag = pct < 25;
    for (let i = 0; i < METER_BREED; i++) {
      zet(plek.balkX + i, METER_RIJ, i < vol ? (laag ? K.rood : kleur) : K.grijs);
    }
  };
  /** Een appeltje: een steeltje en een rood lijfje. */
  const APPEL = [[1, 0, K.groen], [0, 1, K.rood], [1, 1, K.rood], [2, 1, K.rood],
    [0, 2, K.rood], [1, 2, K.rood], [2, 2, K.rood]];
  /** Een hartje: twee bolletjes, een brede rij, en dan een punt. */
  const HART = [[0, 0, K.rood], [2, 0, K.rood],
    [0, 1, K.rood], [1, 1, K.rood], [2, 1, K.rood], [1, 2, K.rood]];
  // Welke laag wordt er gevraagd? Ze sluiten elkaar uit, zodat de tekenaar een
  // lijf zonder gezicht kan uitknippen en de app de lagen weer kan stapelen zonder
  // dat er iets dubbel komt te staan.
  const tekenAchter = deel === 'alles' || deel === 'achter';
  const tekenLijf = deel === 'alles' || deel === 'lijf';
  if (tekenAchter) {
    meter(0, APPEL, stand.honger, K.oranje);
    meter(1, HART, stand.blij, K.groen);
  }

  // De vloer ligt op de onderste rij van het scherm. Hij lag er één boven, en dan
  // bleef er onderaan een lege rij over terwijl het bovenin krap was.
  const vloer = VELD - 1;
  if (tekenAchter) for (let x = 0; x < VELD; x++) zet(x, vloer, K.grijs);

  /**
   * ---- de naam kiezen ----------------------------------------------------
   *
   * Zolang je een naam kiest staan er drie letters op het schermpje, met een
   * streepje onder degene die je aan het draaien bent. Het beestje maakt zolang
   * plaats; op vierentwintig vakjes passen ze niet samen, en op een arcadekast
   * krijgt het invoeren van je naam ook het hele scherm.
   *
   * Drie letters van drie breed met twee kieren is dertien, en dat staat met vijf
   * en zes vakjes lucht aan weerskanten precies in het midden.
   */
  if (naamKiezer) {
    const letters = naamKiezer.letters || ['A', 'A', 'A'];
    const links = Math.floor((VELD - (letters.length * 4 - 1)) / 2);
    const top = Math.floor((VELD - 5) / 2);
    letters.forEach((letter, n) => {
      const glyph = FONT[String(letter || 'A').toUpperCase()] || FONT.A;
      const x0 = links + n * 4;
      glyph.forEach((rij, dy) => {
        for (let dx = 0; dx < rij.length; dx++) {
          if (rij[dx] === '#') zet(x0 + dx, top + dy, K.blauw);
        }
      });
      // Het streepje onder de letter waar je aan draait; het knippert, zodat je
      // ziet welke van de drie aan de beurt is.
      if (n === (naamKiezer.actief || 0) && tik % 2 === 0) {
        for (let dx = 0; dx < 3; dx++) zet(x0 + dx, top + 6, K.rood);
      }
    });
    return uit;
  }

  const wip = stand.slaapt ? 0 : (tik % 2);
  /**
   * Hij blijft niet op zijn plek staan.
   *
   * Een rondje van achttien tikken, dus negen seconden: vier tellen stilstaan,
   * dan twee vakjes naar rechts kuieren, weer stilstaan, en terug. Slapend en als
   * ei staat hij stil, want een ei dat loopt is geen ei.
   *
   * Op tik nul is de stap nul. Daar rekent de symmetrietest op: de middenlijn van
   * het beestje valt dan samen met die van het scherm.
   */
  const WANDEL = [0, 0, 0, 0, 1, 1, 2, 2, 2, 1, 1, 0, 0, -1, -1, -2, -2, -1];
  const stap = stand.slaapt || stand.fase === 'ei' ? 0 : WANDEL[tik % WANDEL.length];
  // Het midden zit tussen twee kolommen, dus reken vanaf een halve pixel: zo
  // blijft alles wat je spiegelt precies even ver van de middenlijn.
  const mx = VELD / 2 + stap;
  const r = MAAT[stand.fase] || 4;
  // Min en niet plus: met plus zakte hij elke tweede tik tot in de vloer. Twee
  // eraf en niet één: onder het lijf staan nog zijn pootjes.
  const my = vloer - 2 - r - wip;

  /** Een pixel links en rechts van de middenlijn, op afstand d (1, 2, 3, …). */
  const paar = (d, y, k) => { zet(mx - d, y, k); zet(mx + d - 1, y, k); };

  if (stand.fase === 'ei') {
    if (!tekenLijf) return uit;
    // Een ei: onder rond, boven smal, met een barst over het midden.
    for (let y = -r - 1; y <= r; y++) {
      const t = (y + 0.5) / (r + 1.5);
      const breed = Math.round((r + 1) * Math.sqrt(Math.max(0, 1 - t * t)) * (1 + 0.2 * t));
      for (let d = 1; d <= breed; d++) paar(d, my + y, d === breed ? K.blauw : K.grijs);
    }
    /**
     * De barst. Eerst waren dit drie streepjes naast elkaar over het midden en
     * dat leek precies op twee ogen en een mond: een ei met een gezicht. Nu is
     * het één schuine lijn die vanuit het midden naar beide kanten omlaag loopt,
     * en een doorlopende diagonaal leest als een scheur en niet als een snuit.
     */
    for (let d = 1; d <= 4; d++) paar(d, my - 3 + d, K.blauw);
    return uit;
  }

  /**
   * ---- het lijf ----------------------------------------------------------
   *
   * Een langwerpig dropje op twee pootjes, met twee donkere oortjes erbovenop.
   *
   * Een echte ellips en geen superellips: bij een macht van 2,4 loopt de zijkant
   * een stuk kaarsrecht en slaat hij daarna in één stap om, en dat zijn de harde
   * hoekjes aan de zijkanten. Bij macht 2 buigt de rand overal even hard en is er
   * nergens een hoek.
   *
   * Absolute waarden, want Math.pow met een negatieve basis en een gebroken macht
   * levert NaN, en dan verdwijnt de hele bovenste helft van het lijf.
   */
  const ry = r;
  const rond = (dx, dy) =>
    Math.pow(Math.abs(dx) / (r + 0.5), 2.2) + Math.pow(Math.abs(dy) / (ry + 0.5), 2.2) <= 1;
  const lijf = new Set();
  const sleutel = (x, y) => x + ',' + y;
  if (tekenLijf) {
  for (let y = -ry; y <= ry; y++) {
    for (let d = 1; d <= r; d++) {
      if (!rond(d - 0.5, y)) continue;
      lijf.add(sleutel(mx - d, my + y));
      lijf.add(sleutel(mx + d - 1, my + y));
    }
  }
  // Licht van binnen, donker aan de rand.
  lijf.forEach((k) => {
    const [x, y] = k.split(',').map(Number);
    const rand = !lijf.has(sleutel(x - 1, y)) || !lijf.has(sleutel(x + 1, y))
      || !lijf.has(sleutel(x, y - 1)) || !lijf.has(sleutel(x, y + 1));
    zet(x, y, rand ? K.blauw : K.grijs);
  });

  /**
   * De oortjes: twee knopjes bovenop, helemaal donker.
   *
   * Ze worden ná de omlijning getekend en niet als deel van het lijf. Zaten ze in
   * het lijf, dan vulde de omlijning ze bij de kleinere fases licht op en werden
   * het holle blokjes; nu zijn ze massief, welke maat hij ook heeft.
   */
  // Naar binnen, zodat ze bovenop zijn kop staan en niet op zijn schouders. Maar
  // niet tot tegen de middenlijn: bij drie blijft er een kuiltje van twee vakjes
  // tussen de twee oortjes. Op twee schoven ze tegen elkaar aan en werd het één
  // donkere balk over zijn kop.
  const oorD = Math.max(3, r - 3);
  const oorH = r >= 5 ? 2 : 1;
  const oorB = r >= 4 ? 2 : 1;
  for (let j = 1; j <= oorH; j++) {
    for (let i = 0; i < oorB; i++) {
      paar(oorD - i, my - ry - j, K.blauw);
    }
  }

  /**
   * Twee pootjes onder het lijf, één rij hoog. Klein met opzet: het zijn voetjes
   * die net onder hem uit komen, geen benen. Zonder die twee zweefde het lijf en
   * stond hij op zijn buik.
   */
  paar(2, my + ry + 1, K.blauw);
  if (r >= 5) paar(3, my + ry + 1, K.blauw);
  }

  /**
   * ---- het gezicht -------------------------------------------------------
   *
   * Laag in het lijf en ver uit elkaar. Ogen hoog en dicht op elkaar maken een
   * gezicht dat streng kijkt; laag en breed maakt het een baby.
   *
   * Knipperen: één tik in de twaalf gaan ze dicht. Dat is elke zes seconden, en
   * dat ene detail is het verschil tussen een plaatje en een wezen. Op tik nul
   * staan ze open, zodat een verse tekening hem niet slapend laat lijken.
   */
  // Twee bij twee kijkende ogen kan pas als het lijf breed genoeg is; daaronder
  // botsen ze op de mond en op de omlijning, en dan is het geen gezicht meer maar
  // een handvol stipjes.
  /**
   * De ogen: hoge rechthoeken, twee breed en drie hoog.
   *
   * Dat is wat hem schattig maakt. Vierkante oogjes of losse stipjes geven een
   * gezicht dat kijkt; hoge ogen geven een gezicht dat opkijkt. Op een klein lijf
   * is er maar één kolom ruimte, dan worden het strepen van één bij drie.
   */
  const groot = r >= 5;
  const oy = my - Math.max(2, Math.round(ry * 0.45));
  const oogH = groot ? 3 : 2;
  // Ogen en mond zijn twee losse stukjes, dus twee lagen: de tekenaar knipt ze uit
  // elkaar en de app zet ze weer samen.
  const tekenOgen = deel === 'alles' || deel === 'ogen';
  const tekenMond = deel === 'alles' || deel === 'mond';
  if (tekenOgen) {
  const oogD = Math.max(2, r - 2);
  const knippert = !stand.slaapt && tik % 12 === 11;
  if (stand.slaapt || knippert) {
    // Dicht: één streepje op de plek van het oog.
    paar(oogD, oy + 1, K.blauw);
    if (groot) paar(oogD - 1, oy + 1, K.blauw);
  } else {
    for (let dy = 0; dy < oogH; dy++) {
      paar(oogD, oy + dy, K.blauw);
      if (groot) paar(oogD - 1, oy + dy, K.blauw);
    }
  }
  }

  /**
   * ---- de mond -----------------------------------------------------------
   *
   * Een streepje van vier, één rij hoog. Geen boog en geen open mond: op dit
   * formaat leest een klein donker streepje onder twee hoge ogen als een tevreden
   * snuit, en alles wat groter is wordt een gat in zijn gezicht.
   *
   * De blosjes en de buikvlekjes zijn eruit. Ze maakten hem drukker, niet liever.
   */
  const my2 = oy + oogH + 1;
  if (tekenMond) {
  if (stand.humeur === 'hongerig' || stand.humeur === 'ziek') {
    // Een open mondje: hij vraagt erom.
    paar(1, my2, K.blauw);
    paar(1, my2 + 1, K.blauw);
  } else if (stand.humeur === 'verdrietig') {
    // Hoeken omlaag.
    paar(1, my2, K.blauw);
    paar(2, my2 + 1, K.blauw);
  } else {
    paar(1, my2, K.blauw);
    paar(2, my2, K.blauw);
  }
  }


  // ---- wat er om hem heen gebeurt ---------------------------------------
  const tekenExtra = deel === 'alles' || deel === 'extra';
  if (!tekenExtra) {
    // niets
  } else if (actie === 'voeren') {
    /**
     * Een appeltje voor zijn neus: rood lijfje, een steeltje en een blaadje.
     * Vijf bij vijf is het kleinste dat nog als appel leest.
     */
    const ax = 0, ay = vloer - 6;
    // Steeltje en blaadje aan de linkerkant: rechts staat het beestje, en daar
    // vielen ze achter hem weg.
    zetAchter(ax + 1, ay, K.wijn);
    zetAchter(ax, ay + 1, K.groen);
    for (const [dx, dy] of [[1, 1], [2, 1], [0, 2], [1, 2], [2, 2], [3, 2],
      [0, 3], [1, 3], [2, 3], [3, 3], [1, 4], [2, 4]]) {
      zetAchter(ax + dx, ay + dy, K.rood);
    }
  } else if (actie === 'aaien') {
    /**
     * Een hartje, symmetrisch om de middenlijn en zo simpel als het kan: twee
     * bolletjes, een brede rij, en dan in twee stappen naar een punt.
     *
     * Het stond eerst linksboven om het beestje niet te raken, en dat was scheef.
     * Nu ligt het gewoon in het midden, een beetje over zijn kruin. Een hartje
     * vóór het beestje is precies wat een echte tamagotchi ook doet.
     */
    const hy = Math.max(KOP, my - r - 2);
    paar(2, hy, K.rood);
    paar(3, hy, K.rood);
    for (let d = 1; d <= 3; d++) paar(d, hy + 1, K.rood);
    for (let d = 1; d <= 2; d++) paar(d, hy + 2, K.rood);
    paar(1, hy + 3, K.rood);
  } else if (actie === 'spelen') {
    /**
     * Bij spelen zie je een spelletje: een balletje dat heen en weer stuitert.
     *
     * Zo simpel als het kan en toch te volgen. Het balletje is twee bij twee en
     * springt per tik naar een volgende plek in een boogje; drie plekken heen en
     * drie terug, dus je ziet hem echt kaatsen in plaats van knipperen. Een
     * hartje zei "leuk", dit zegt "we zijn iets aan het doen".
     */
    /**
     * Een voetbal van vijf bij vijf, en niet meer een oranje blokje van twee.
     *
     * Wit lijfje met een donkere lap in het midden: dat is het kleinste dat als
     * voetbal leest. Op twee bij twee kon dat niet, want daar is elke pixel een
     * kwart van de bal en past er geen patroon in. Het lichte is `grijs`, want dat
     * is het wit van dit palet.
     *
     * De baan is een boog van links onder, over zijn kruin, naar rechts onder.
     * Zes plekken, dus je ziet hem stuiteren in plaats van knipperen, en hij
     * blijft onder de meters.
     */
    const baan = [[2, 11], [5, 7], [9, 5], [13, 5], [17, 7], [19, 11]];
    const [bx, by] = baan[tik % baan.length];
    /**
     * De lappen aan de zijkanten en één in het midden, niet in een kruis: met een
     * donkere rij én een donkere kolom door het midden las de bal als een plusteken
     * en niet als een bal. Nu staan er vier lapjes om een middenstip, zoals de
     * naden van een echte voetbal.
     */
    const BAL = [
      '.XXX.',
      'X0X0X',
      'XX0XX',
      'X0X0X',
      '.XXX.'
    ];
    BAL.forEach((rij, dy) => {
      for (let dx = 0; dx < rij.length; dx++) {
        if (rij[dx] === '.') continue;
        zet(bx + dx, by + dy, rij[dx] === '0' ? K.zwart : K.grijs);
      }
    });
  } else if (stand.slaapt) {
    /**
     * Een Z'je dat opstijgt, naast zijn hoofd en niet erbuiten.
     *
     * Drie breed en vijf hoog, met de diagonaal erin: een streep boven, een
     * streep onder en de schuine lijn ertussen. Het was twee bij drie en dat is te
     * klein voor een diagonaal, dus je zag een blokje met een hap eruit in plaats
     * van een letter. In lichtblauw, want de omlijning van het beestje zelf is al
     * donkerblauw.
     */
    const ZED = ['XXX', '..X', '.X.', 'X..', 'XXX'];
    const zx = Math.min(VELD - 4, mx + r);
    const zy = Math.max(KOP, my - r - 5 - (tik % 2));
    ZED.forEach((rij, dy) => {
      for (let dx = 0; dx < rij.length; dx++) {
        if (rij[dx] === 'X') zet(zx + dx, zy + dy, K.lucht);
      }
    });
  } else if (stand.humeur === 'ziek') {
    // Een wolkje van vier met een drupje eronder.
    const wy = Math.max(KOP, my - r - 3);
    paar(1, wy, K.wijn);
    paar(2, wy, K.wijn);
    zet(mx - 1, wy + 1, K.wijn);
    zet(mx, wy + 1, K.wijn);
  } else if (stand.humeur === 'blij' && tik % 2) {
    // Twee streepjes vrolijkheid, alleen op de ene tik.
    zet(mx - r - 2, my - r, K.oranje);
    zet(mx + r + 1, my - r, K.oranje);
  }

  /**
   * De hoopjes als laatste, zodat het beestje ze niet afdekt.
   *
   * Drie pixels breed met er één bovenop: twee losse pixels waren een streepje
   * en las niemand als een hoopje.
   */
  for (let n = 0; tekenExtra && n < Math.min(stand.hoopjes, HOOP_MAX); n++) {
    const hx = n === 0 ? 1 : VELD - 4;
    for (let dx = 0; dx < 3; dx++) zet(hx + dx, vloer - 1, K.wijn);
    zet(hx + 1, vloer - 2, K.wijn);
    /**
     * En er zoemt een vliegje omheen.
     *
     * Eén pixel die per tik van plek wisselt, vier plekken rond het hoopje. Een
     * hoopje dat er alleen maar ligt vraagt er niet om opgeruimd te worden; een
     * vliegje erboven wel. Slapend zoemt hij niet: dan is het stil in het hok.
     */
    if (stand.slaapt) continue;
    const rondje = [[-1, -3], [1, -4], [3, -3], [1, -2]];
    const [vx, vy] = rondje[(tik + n * 2) % rondje.length];
    zet(hx + 1 + vx, vloer + vy, K.zwart);
  }
  return uit;
}

// ---- losse stukjes, met de hand getekend --------------------------------

/**
 * Het beestje is opgebouwd uit stukjes, en die kun je met de hand tekenen.
 *
 * Elk stukje staat op een doekje van 16 bij 16 met doorzichtige vakjes. De app
 * zet ze op hun plek: het lijf in het midden van het beestje, de ogen en de mond
 * op zijn gezicht, en elk object daar waar het hoort. Zo hoef jij alleen te
 * tekenen hoe iets eruitziet en niet uit te rekenen waar het staat.
 *
 * Waarom niet één heel doek per plaatje: dan moet je van elk gezicht in elke
 * fase een compleet beestje tekenen, en dat zijn er veertig. Zo zijn het er
 * zeventien, en een nieuwe mond kost één tekening in plaats van twaalf.
 *
 * Is een stukje niet getekend, dan tekent de code het zoals hij het altijd deed.
 * Je kunt er dus één beginnen en de rest laten staan.
 */
/**
 * Elk stukje heeft zijn eigen maat.
 *
 * Een vliegje op een doek van 16 bij 16 is één pixel in een leeg zwembad; een lijf
 * heeft die ruimte wel nodig. Vandaar per stukje een breedte en een hoogte die
 * past bij wat het is, met wat lucht eromheen zodat je nog kunt uitpakken. Het
 * doekje in de tekenaar krimpt mee en de vakjes worden dan juist groter.
 *
 * De maten zijn ruimer dan wat de code nu tekent: dit zijn maxima, geen mallen.
 */
export const STUK = 16;

/**
 * De stukjes, met waar ze aan vastzitten.
 *
 * `anker` zegt op welk punt van het beestje het midden van het doekje landt:
 * - `lijf`   het midden van het beestje
 * - `ogen`   zijn ogen
 * - `mond`   zijn mond
 * - `boven`  net boven zijn kruin, voor een hartje of een wolkje
 * - `naast`  naast zijn hoofd, voor het slaapteken
 * - `vloer`  op de grond, voor het appeltje en een hoopje
 * - `baan`   waar het balletje op dat moment stuitert
 * - `vlieg`  waar het vliegje op dat moment zoemt
 */
export const GROEPEN = [
  { id: 'beestje', naam: 'Beestje' },
  { id: 'gezicht', naam: 'Gezicht' },
  { id: 'object', naam: 'Objecten' }
];

export const STUKKEN = [
  { sleutel: 'ei', naam: 'Het ei', anker: 'lijf', groep: 'beestje', b: 14, h: 16 },
  { sleutel: 'lijf-baby', naam: 'Lijf: baby', anker: 'lijf', groep: 'beestje', b: 14, h: 18 },
  { sleutel: 'lijf-kind', naam: 'Lijf: kind', anker: 'lijf', groep: 'beestje', b: 16, h: 20 },
  { sleutel: 'lijf-groot', naam: 'Lijf: groot', anker: 'lijf', groep: 'beestje', b: 18, h: 22 },
  { sleutel: 'ogen', naam: 'Ogen: open', anker: 'ogen', groep: 'gezicht', b: 12, h: 4 },
  { sleutel: 'ogen-dicht', naam: 'Ogen: dicht', anker: 'ogen', groep: 'gezicht', b: 12, h: 4 },
  { sleutel: 'mond-blij', naam: 'Mond: blij', anker: 'mond', groep: 'gezicht', b: 8, h: 4 },
  { sleutel: 'mond-honger', naam: 'Mond: honger', anker: 'mond', groep: 'gezicht', b: 8, h: 4 },
  { sleutel: 'mond-verdrietig', naam: 'Mond: verdrietig', anker: 'mond', groep: 'gezicht', b: 8, h: 4 },
  { sleutel: 'mond-slaap', naam: 'Mond: slaapt', anker: 'mond', groep: 'gezicht', b: 8, h: 4 },
  { sleutel: 'appel', naam: 'Appeltje', anker: 'vloer', groep: 'object', b: 7, h: 7 },
  { sleutel: 'hartje', naam: 'Hartje', anker: 'boven', groep: 'object', b: 7, h: 7 },
  { sleutel: 'bal', naam: 'Voetbal', anker: 'baan', groep: 'object', b: 7, h: 7 },
  { sleutel: 'zzz', naam: 'Slaapteken', anker: 'naast', groep: 'object', b: 5, h: 7 },
  { sleutel: 'wolkje', naam: 'Wolkje: ziek', anker: 'wolk', groep: 'object', b: 5, h: 5 },
  { sleutel: 'hoopje', naam: 'Hoopje', anker: 'hoop', groep: 'object', b: 5, h: 5 },
  { sleutel: 'vlieg', naam: 'Vliegje', anker: 'vlieg', groep: 'object', b: 5, h: 5 }
];

/**
 * Er zijn zes beestjes, en welke je krijgt kies je niet.
 *
 * Bij het uitkomen van je ei valt er een van de zes. Dat is de hele lol: je weet
 * pas wie er in zat als hij eruit komt, en je kunt niet net zolang opnieuw
 * beginnen tot je de leukste hebt. Elk beestje heeft zijn eigen zeventien stukjes,
 * dus zes keer een lijf, een gezicht en zijn eigen spulletjes.
 */
export const SOORTEN = 5;

/**
 * Het zesde vak is geen beestje maar de spullenbak.
 *
 * Objecten zijn van iedereen: één appeltje, één bal, één hartje, voor alle vijf de
 * beestjes hetzelfde. Ze per beestje laten tekenen betekende vijf keer hetzelfde
 * appeltje, en dan is er altijd één die je vergeet. Vandaar dat ze een eigen vak
 * kregen op de plek waar het zesde beestje stond.
 */
export const OBJECT_SLOT = SOORTEN + 1;

/** Welk beestje hoort bij deze regel? Buiten bereik valt terug op de eerste. */
export function soortVanBeest(beest) {
  const n = Math.round(Number(beest && beest.soort));
  return n >= 1 && n <= SOORTEN ? n : 1;
}

/**
 * De sleutel waaronder een stukje op de server staat: wie het is en welk stukje,
 * met een dubbele punt ertussen.
 *
 * Wie het is, is je accountId: `gsaeu7g4wn07v5v:lijf-groot`. Elk beestje heeft zijn
 * eigen tekening, dus verander je een oog, dan verandert alleen jouw beestje.
 *
 * Hier stond een nummer van 1 tot 5: het soort beestje. De tekeningen waren dus van
 * iedereen samen, en een oog bijwerken keek alle beestjes van dat soort anders aan.
 * Die vijf sleutels bestaan nog en worden nog gelezen — ze zijn de standaard waar
 * een beestje op terugvalt zolang jij niets eigen hebt getekend — maar de tekenaar
 * biedt ze niet meer aan.
 *
 * Objecten blijven van iedereen: een appeltje is een appeltje, en die staan onder
 * `6:` in de gedeelde spullenbak.
 *
 * @param eigenaar  een accountId, of een getal 1 tot 5 voor de oude standaard
 */
export function stukSleutel(eigenaar, sleutel) {
  const stuk = stukVan(sleutel);
  // Een object hoort bij niemand in het bijzonder en staat altijd in het zesde vak.
  if (stuk && stuk.groep === 'object') return OBJECT_SLOT + ':' + sleutel;
  // Een getal is een van de vijf oude soorten; al het andere is een accountId en
  // gaat er ongemoeid in. Zonder deze splitsing maakte `soortVanBeest` van elk
  // accountId een 1, want `Number('gsaeu...')` is NaN.
  const kaal = String(eigenaar || '');
  const n = /^[0-9]+$/.test(kaal) ? soortVanBeest({ soort: Number(kaal) }) : kaal;
  return n + ':' + sleutel;
}

/**
 * Is dit een accountId en niet een van de oude soortnummers?
 *
 * Een id van PocketBase is precies vijftien tekens, kleine letters en cijfers. Die
 * vorm is streng met opzet: zo kan `stukOk` een echte eigenaar onderscheiden van een
 * typfout, en blijft een appeltje onder een beestjesleutel afgekeurd.
 */
export function eigenaarOk(deel) {
  return /^[a-z0-9]{15}$/.test(String(deel || ''));
}

/** Het stukje bij een sleutel, of niets. De sleutel mag het nummer bevatten. */
export function stukVan(sleutel) {
  const kaal = String(sleutel || '').split(':').pop();
  return STUKKEN.find((s) => s.sleutel === kaal) || null;
}

/** Een leeg doekje van de juiste maat. */
export function leegStuk(stuk) {
  return stuk ? '.'.repeat(stuk.b * stuk.h) : '';
}

/**
 * Waar elk stukje landt, gerekend vanuit de stand van het beestje.
 *
 * Eén plek waar deze getallen staan, want twee dingen hangen ervan af: de app die
 * de stukjes op hun plaats zet, en de tekenaar die laat zien wat er nu staat. Zou
 * elk zijn eigen som doen, dan schuift een getekend oog na een wijziging over zijn
 * neus.
 *
 * De punten zijn het midden van het doekje van 16 bij 16, dus het stukje wordt
 * neergezet op x - 8 en y - 8.
 */
export function ankersVan(stand, tik = 0) {
  const vloer = VELD - 1;
  const wip = stand.slaapt ? 0 : (tik % 2);
  const WANDEL = [0, 0, 0, 0, 1, 1, 2, 2, 2, 1, 1, 0, 0, -1, -1, -2, -2, -1];
  const stap = stand.slaapt || stand.fase === 'ei' ? 0 : WANDEL[tik % WANDEL.length];
  const mx = VELD / 2 + stap;
  const r = MAAT[stand.fase] || 4;
  const ry = r;
  const my = vloer - 2 - r - wip;
  const oy = my - Math.max(2, Math.round(ry * 0.45));
  const oogH = r >= 5 ? 3 : 2;
  const baan = [[2, 11], [5, 7], [9, 5], [13, 5], [17, 7], [19, 11]];
  const bal = baan[tik % baan.length];
  const rondje = [[-1, -3], [1, -4], [3, -3], [1, -2]];
  const vlieg = rondje[tik % rondje.length];
  return {
    lijf: { x: mx, y: my },
    ogen: { x: mx, y: oy + 1 },
    mond: { x: mx, y: oy + oogH + 1 },
    /**
     * De min-één-tjes hier zijn geen slordigheid maar de knipkant.
     *
     * Een doekje staat met zijn midden op het ankerpunt, dus als het een pixel
     * smaller wordt valt die pixel er links af en niet rechts. De objecten hadden
     * lucht aan hun rechter- en onderkant, dus schuift het anker een pixel mee: dan
     * blijft de linkerbovenhoek staan waar hij stond en verdwijnt de lege kant.
     */
    boven: { x: mx - 1, y: Math.max(KOP, my - r - 2) + 1 },
    // Het wolkje zat op `boven` en heeft nu zijn eigen plek, zodat het hartje kon
    // krimpen zonder het wolkje mee te slepen.
    wolk: { x: mx, y: Math.max(KOP, my - r - 3) + 1 },
    naast: { x: Math.min(VELD - 4, mx + r), y: Math.max(KOP, my - r - 5 - wip) + 1 },
    vloer: { x: 3, y: vloer - 5 },
    /**
     * Het hoopje ligt lager en linkser dan het appeltje.
     *
     * Ze deelden eerst het anker `vloer`, en dat kon niet: het appeltje staat vóór
     * zijn neus en het hoopje ligt in de hoek. Eén venster om allebei viel of naast
     * het een of naast het ander.
     */
    hoop: { x: 2, y: vloer - 2 },

    baan: { x: bal[0] + 1, y: bal[1] + 1 },
    vlieg: { x: 2 + vlieg[0], y: vloer + vlieg[1] }
  };
}

/**
 * Een naam van drie letters op het schermpje, in pixels.
 *
 * Dezelfde plaatsing als de naamkiezer gebruikt: drie letters van drie breed met een
 * kier ertussen is elf, en dat staat met zes vakjes lucht aan weerskanten in het
 * midden. Zo staat een naam altijd op dezelfde plek, of je hem nu kiest of van een
 * ander leest.
 *
 * @param y  de bovenste rij van de letters
 */
export function naamPixels(naam, y, kleur) {
  const letters = String(naamOk(naam) || '').toUpperCase().split('').slice(0, NAAM_MAX);
  if (!letters.length) return [];
  const links = Math.floor((VELD - (letters.length * 4 - 1)) / 2);
  const uit = [];
  letters.forEach((letter, n) => {
    const glyph = FONT[letter] || FONT.A;
    glyph.forEach((rij, dy) => {
      for (let dx = 0; dx < rij.length; dx++) {
        // Het lettertype tekent met een hekje, niet met een X. Zie FONT.
        if (rij[dx] !== '#') continue;
        const x = links + n * 4 + dx;
        const py = y + dy;
        if (x < 0 || py < 0 || x >= VELD || py >= VELD) continue;
        uit.push({ x, y: py, k: typeof kleur === 'number' ? kleur : K.blauw });
      }
    });
  });
  return uit;
}

/**
 * Een vlak vullen vanaf één vakje.
 *
 * Alles wat er in één stuk aan vastzit en dezelfde kleur heeft wordt de nieuwe
 * kleur. Recht boven, onder, links en rechts, dus niet schuin: op een raster van
 * twaalf bij zes zou schuin vullen door de dunste omlijning heen lekken, en dan
 * loopt je verf over het hele doekje uit.
 *
 * Doorzichtig is ook een kleur. Zo vul je de lucht om een lijf net zo goed als het
 * lijf zelf, en werkt de gum met de emmer erbij als een vlakgum.
 *
 * @param tekst  het doekje
 * @param stuk   welk stukje, voor de breedte en de hoogte
 * @param i      het vakje waar je klikte
 * @param teken  de nieuwe kleur, of '.' voor doorzichtig
 */
export function vulStuk(tekst, stuk, i, teken) {
  const s = String(tekst || '');
  if (!stuk || s.length !== stuk.b * stuk.h) return s;
  if (!(i >= 0 && i < s.length)) return s;
  const oud = s[i];
  // Al de goede kleur: dan valt er niets te vullen, en zonder deze regel loopt de
  // lus hieronder eeuwig door omdat elk vakje "veranderd" blijft heten.
  if (oud === teken) return s;
  const uit = s.split('');
  const stapel = [i];
  while (stapel.length) {
    const n = stapel.pop();
    if (uit[n] !== oud) continue;
    uit[n] = teken;
    const x = n % stuk.b;
    const y = Math.floor(n / stuk.b);
    if (x > 0) stapel.push(n - 1);
    if (x < stuk.b - 1) stapel.push(n + 1);
    if (y > 0) stapel.push(n - stuk.b);
    if (y < stuk.h - 1) stapel.push(n + stuk.b);
  }
  return uit.join('');
}

/**
 * Een doekje van de vorige maat bijknippen.
 *
 * De gezichtjes zijn een pixel aan elke kant kleiner geworden: de ogen van 14 bij 6
 * naar 12 bij 4, de monden van 10 bij 6 naar 8 bij 4. Wat al getekend was staat nog
 * op de oude maat op de server, en zonder deze knip was dat stilletjes weg geweest,
 * want een doekje met de verkeerde lengte telt niet als doekje.
 *
 * Het venster krimpt om zijn midden, dus de nieuwe maat is precies de oude min de
 * buitenste ring. Vandaar dat er niets verschuift: wat je tekende blijft staan waar
 * het stond. Alleen wat je in die ring tekende valt eraf, en daar was ook geen plek
 * meer voor.
 *
 * Deze mag weg zodra alle zes de beestjes op de nieuwe maat bewaard zijn.
 */
export function krimpStuk(tekst, stuk) {
  const s = String(tekst || '');
  if (!stuk) return null;
  /**
   * Twee maten zijn er ooit kleiner geworden, elk op hun eigen manier.
   *
   * De gezichtjes gingen een ring kleiner, een pixel aan alle vier de kanten: dat
   * venster krimpt om zijn midden, dus de knip begint op één, één.
   *
   * De objecten leverden alleen rechts en onder een rij in, met hun ankerpunt een
   * pixel mee zodat de linkerbovenhoek bleef staan: die knip begint op nul, nul.
   */
  const vormen = [
    { b: stuk.b + 2, h: stuk.h + 2, dx: 1, dy: 1 },
    { b: stuk.b + 1, h: stuk.h + 1, dx: 0, dy: 0 }
  ];
  const vorm = vormen.find((v) => s.length === v.b * v.h);
  if (!vorm) return null;
  let uit = '';
  for (let y = 0; y < stuk.h; y++) {
    const van = (y + vorm.dy) * vorm.b + vorm.dx;
    uit += s.slice(van, van + stuk.b);
  }
  return uit;
}

/**
 * Een stuk van een doekje oppakken en ergens anders neerzetten.
 *
 * Wat er stond wordt doorzichtig en verschijnt op zijn nieuwe plek. Wat buiten het
 * doekje valt gaat verloren, net als bij een schaar: je kunt niet tekenen op een
 * vakje dat er niet is.
 *
 * `tot` is de eerste rij die niet meer beschreven mag worden, want daar begint de
 * vloer. Zonder die grens kon je een lijf de grond in schuiven.
 *
 * @param rect  { x0, y0, x1, y1 } in vakjes, allebei de hoeken meegerekend
 */
export function verplaatsStuk(tekst, stuk, rect, dx, dy, tot) {
  const s = String(tekst || '');
  if (!stuk || !rect || s.length !== stuk.b * stuk.h) return s;
  const grens = typeof tot === 'number' ? tot : stuk.h;
  const uit = s.split('');
  const knip = [];
  for (let y = rect.y0; y <= rect.y1; y++) {
    for (let x = rect.x0; x <= rect.x1; x++) {
      if (x < 0 || y < 0 || x >= stuk.b || y >= grens) continue;
      knip.push({ x, y, c: uit[y * stuk.b + x] });
      uit[y * stuk.b + x] = '.';
    }
  }
  knip.forEach((q) => {
    const x = q.x + dx;
    const y = q.y + dy;
    if (x < 0 || y < 0 || x >= stuk.b || y >= grens) return;
    uit[y * stuk.b + x] = q.c;
  });
  return uit.join('');
}

/** Is dit een geldige sleutel? Bewaart de server tegen onzin. */
export function stukOk(sleutel) {
  const delen = String(sleutel || '').split(':');
  if (delen.length !== 2) return false;
  const stuk = STUKKEN.find((s) => s.sleutel === delen[1]);
  if (!stuk) return false;
  const n = Number(delen[0]);
  // Streng, en met opzet: een appeltje onder een beestjesleutel is een fout uit de
  // tijd dat elk beestje zijn eigen spullen had, en die willen we niet terug.
  if (stuk.groep === 'object') return n === OBJECT_SLOT;
  // Een beestjestukje staat onder een accountId (jouw eigen tekening) of onder een
  // van de vijf oude soortnummers (de standaard waar je op terugvalt).
  return eigenaarOk(delen[0]) || (n >= 1 && n <= SOORTEN);
}

/**
 * Een doekje inlezen: van een tekenreeks naar losse pixels.
 *
 * Een punt is doorzichtig, de cijfers 0 tot en met 7 zijn de kleuren van het
 * palet. Klopt de lengte niet, dan is het geen doekje en krijg je niets terug;
 * beter geen stukje dan een half stukje.
 */
export function stukPixels(tekst, stuk) {
  const s = String(tekst || '');
  if (!stuk || s.length !== stuk.b * stuk.h) return null;
  const uit = [];
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c < '0' || c > '7') continue;
    uit.push({ x: i % stuk.b, y: Math.floor(i / stuk.b), k: Number(c) });
  }
  return uit;
}

/** En terug: van losse pixels naar een tekenreeks. */
export function stukTekst(pixels, stuk) {
  if (!stuk) return '';
  const veld = new Array(stuk.b * stuk.h).fill('.');
  (pixels || []).forEach((p) => {
    if (p.x < 0 || p.y < 0 || p.x >= stuk.b || p.y >= stuk.h) return;
    veld[p.y * stuk.b + p.x] = String(p.k);
  });
  return veld.join('');
}

/**
 * Het hele beeld, met de getekende stukjes waar die er zijn.
 *
 * Dit is de plek waar de tekenaar en de code samenkomen. Per laag geldt: is er een
 * stukje getekend voor dit beestje, dan komt dat op zijn ankerpunt; is er niets,
 * dan tekent de code die laag zoals altijd. Zo kun je één mond tekenen en de rest
 * laten staan, en zie je meteen het verschil.
 *
 * `stukken` is een kaart van sleutel (`3:mond-blij`) naar tekenreeks, precies zoals
 * hij van de server komt.
 */
export function beeldVan(stand, tik = 0, actie = null, stukken = null, soort = 1, eigenaar = '') {
  const kaart = stukken || {};
  const ankers = ankersVan(stand, tik);
  const uit = [];

  /** Een getekend stukje op zijn ankerpunt. Levert false als het er niet is. */
  const leg = (naam) => {
    const stuk = stukVan(naam);
    /**
     * Drie kansen, en de eerste die iets oplevert wint.
     *
     * 1. Wat de eigenaar zelf getekend heeft. Elk beestje heeft zijn eigen tekening,
     *    dus dit is wat jouw beestje van iedereen anders onderscheidt.
     * 2. De standaard van zijn soort, 1 tot 5. Die tekeningen zijn van vroeger, toen
     *    de vijf soorten samen werden getekend; ze blijven de basis waar een beestje
     *    op staat zolang de eigenaar er niets van gemaakt heeft.
     * 3. Die van soort één. Dat is de standaard van de standaard: zo ziet een beestje
     *    eruit als er verder niets over gezegd is, en het scheelt vier keer hetzelfde
     *    natekenen.
     *
     * Een leeggemaakt doekje telt als "niets van mezelf" en niet als "onzichtbaar":
     * zo laat je een beestje weer de standaard volgen zonder een regel te wissen. Dat
     * geldt op elke trede.
     *
     * Objecten doen hier niet aan mee: die staan in de gedeelde spullenbak, dus
     * stukSleutel geeft daar op alle drie de treden hetzelfde.
     */
    const beurt = (wie) => {
      if (!stuk || wie === '' || wie === null || wie === undefined) return null;
      const pix2 = stukPixels(kaart[stukSleutel(wie, naam)], stuk);
      return pix2 && pix2.length ? pix2 : null;
    };
    const pix = beurt(eigenaar) || beurt(soort) || beurt(1);
    if (!pix || !pix.length) return false;
    const anker = ankers[stuk.anker] || { x: VELD / 2, y: VELD / 2 };
    const x0 = anker.x - Math.floor(stuk.b / 2);
    const y0 = anker.y - Math.floor(stuk.h / 2);
    pix.forEach((p) => {
      const x = x0 + p.x;
      const y = y0 + p.y;
      if (x < 0 || y < 0 || x >= VELD || y >= VELD) return;
      uit.push({ x, y, k: p.k });
    });
    return true;
  };

  /** Een laag uit de code, als er geen tekening voor is. */
  const uitCode = (welke) => pixels(stand, tik, actie, null, welke).forEach((p) => uit.push(p));

  // De meters en de vloer komen altijd uit de code: die bewegen mee met de stand
  // en zijn geen tekening.
  uitCode('achter');

  const lijfNaam = stand.fase === 'ei' ? 'ei' : 'lijf-' + stand.fase;
  if (!leg(lijfNaam)) uitCode('lijf');

  if (stand.fase !== 'ei') {
    const dicht = stand.slaapt || tik % 12 === 11;
    if (!leg(dicht ? 'ogen-dicht' : 'ogen')) uitCode('ogen');
    const mond = stand.humeur === 'hongerig' || stand.humeur === 'ziek' ? 'mond-honger'
      : stand.humeur === 'verdrietig' ? 'mond-verdrietig'
        : stand.slaapt ? 'mond-slaap' : 'mond-blij';
    if (!leg(mond)) uitCode('mond');
  }

  /**
   * En alles wat er omheen gebeurt.
   *
   * Eén laag in de code, maar in de tekenaar losse stukjes. Is het stukje dat bij
   * dit moment hoort getekend, dan komt dat; is het er niet, dan tekent de code de
   * hele laag in één keer. Half om half kan niet: dan zou je het hoopje uit de
   * tekening en het vliegje uit de code krijgen, op twee plekken.
   */
  const nu = actie === 'voeren' ? 'appel' : actie === 'aaien' ? 'hartje'
    : actie === 'spelen' ? 'bal' : stand.slaapt ? 'zzz'
      : stand.humeur === 'ziek' ? 'wolkje' : '';
  const hoopjes = Math.min(stand.hoopjes || 0, HOOP_MAX);
  const heeft = (naam) => {
    if (!naam) return false;
    const stuk = stukVan(naam);
    const eigen = stukPixels(kaart[stukSleutel(soort, naam)], stuk);
    if (eigen && eigen.length) return true;
    return !!(stukPixels(kaart[stukSleutel(1, naam)], stuk) || []).length;
  };
  if ((nu && heeft(nu)) || (hoopjes && heeft('hoopje'))) {
    if (nu) leg(nu);
    for (let n = 0; n < hoopjes; n++) {
      if (!leg('hoopje')) break;
      if (!stand.slaapt) leg('vlieg');
    }
  } else {
    uitCode('extra');
  }
  return uit;
}
